import { useEffect } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

const NAV = [
  { to: '/dashboard', label: 'Dashboard', icon: '▦' },
  { to: '/posts',     label: 'Posts',     icon: '≡' },
]

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation()

  // Close sidebar whenever the route changes (mobile nav tap)
  useEffect(() => {
    onClose()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  return (
    <aside className={`sidebar${isOpen ? ' sidebar--open' : ''}`}>
      <div className="sidebar-logo">
        <span className="sidebar-logo-icon">{'</>'}</span>
        <span className="sidebar-logo-text">CodeToByte</span>
        <span className="sidebar-logo-sub">Admin</span>
        <button
          className="sidebar-close"
          onClick={onClose}
          aria-label="Close menu"
        >
          ✕
        </button>
      </div>

      <nav className="sidebar-nav">
        {NAV.map(({ to, label, icon }) => (
          <NavLink
            key={to}
            to={to}
            className={({ isActive }) =>
              ['sidebar-link', isActive ? 'sidebar-link--active' : ''].join(' ').trim()
            }
          >
            <span className="sidebar-link-icon">{icon}</span>
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="sidebar-footer">
        <span className="sidebar-version">v1.0 · admin</span>
      </div>

      <style>{`
        .sidebar {
          width: var(--sidebar-width);
          min-height: 100vh;
          background: var(--bg-sidebar);
          border-right: 1px solid var(--border);
          display: flex;
          flex-direction: column;
          flex-shrink: 0;
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          z-index: 30;
          transition: transform 0.25s ease;
        }
        .sidebar-logo {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 18px 16px 16px;
          border-bottom: 1px solid var(--border);
          flex-wrap: nowrap;
        }
        .sidebar-logo-icon {
          font-family: var(--font-mono);
          font-size: 1rem;
          color: var(--accent);
          font-weight: 700;
          flex-shrink: 0;
        }
        .sidebar-logo-text {
          font-weight: 700;
          font-size: 0.9rem;
          color: var(--text-primary);
          letter-spacing: 0.02em;
          flex-shrink: 0;
        }
        .sidebar-logo-sub {
          font-size: 0.65rem;
          color: var(--text-muted);
          font-family: var(--font-mono);
          text-transform: uppercase;
          letter-spacing: 0.1em;
          flex-shrink: 0;
        }
        .sidebar-close {
          display: none;
          margin-left: auto;
          padding: 4px 8px;
          color: var(--text-muted);
          font-size: 0.9rem;
          border-radius: var(--radius-sm);
          transition: color 0.15s, background 0.15s;
          flex-shrink: 0;
        }
        .sidebar-close:hover {
          color: var(--text-primary);
          background: var(--bg-hover);
        }
        .sidebar-nav {
          flex: 1;
          padding: 12px 8px;
          display: flex;
          flex-direction: column;
          gap: 2px;
          overflow-y: auto;
        }
        .sidebar-link {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border-radius: var(--radius-md);
          color: var(--text-secondary);
          font-size: 0.875rem;
          font-weight: 500;
          transition: background 0.15s, color 0.15s;
          text-decoration: none;
        }
        .sidebar-link:hover {
          background: var(--bg-hover);
          color: var(--text-primary);
          text-decoration: none;
        }
        .sidebar-link--active {
          background: var(--accent-subtle);
          color: var(--accent);
        }
        .sidebar-link--active:hover {
          background: var(--accent-subtle);
          color: var(--accent);
        }
        .sidebar-link-icon {
          width: 18px;
          text-align: center;
          font-size: 1rem;
          opacity: 0.85;
          flex-shrink: 0;
        }
        .sidebar-footer {
          padding: 12px 20px;
          border-top: 1px solid var(--border);
        }
        .sidebar-version {
          font-size: 0.7rem;
          color: var(--text-muted);
          font-family: var(--font-mono);
        }

        /* ── Mobile ── */
        @media (max-width: 768px) {
          .sidebar {
            transform: translateX(-100%);
          }
          .sidebar.sidebar--open {
            transform: translateX(0);
          }
          .sidebar-close {
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      `}</style>
    </aside>
  )
}
